var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react';
import { ConfigProvider, Typography, Button, message as antMessage } from 'antd';
import Icon, { LoadingOutlined } from '@ant-design/icons';
import { Trans, useTranslation } from 'react-i18next';
import { useStore } from '../../../../store/rootStore';
import QuickRepliesField from './components/QuickRepliesField/QuickRepliesField';
import AddOrEditReplyField from './components/AddOrEditReplyField/AddOrEditReplyField';
import DeleteConfirmation from './components/DeleteConfirmation';
import Header from '../../../QuickReplies/components/Header';
import Filters from '../../../QuickReplies/components/Filters';
import ChannelSelect from '../../ChatsList/ChannelSelect';
import { matchesQuickReplyDepartment, matchesQuickReplySearch } from '../../../QuickReplies/helpers';
import { getDirection, updateLocalStorage } from '../../../../helpers';
import { LOCAL_STORAGE_KEYS } from '../../../../constants';
import { ReactComponent as QuickRepliesFolder } from '../../../../assets/icons/file/folderIcon.svg';
import { ReactComponent as QuickRepliesPlus } from '../../../../assets/icons/plus/quickRepliesPlus.svg';
import './QuickReplies.scss';
const { Text } = Typography;
const QuickRepliesMenu = (props) => {
    var _a, _b, _c;
    const chatChannelName = (_a = props.channelInfo) === null || _a === void 0 ? void 0 : _a.name;
    const chatAccountId = (_b = props.channelInfo) === null || _b === void 0 ? void 0 : _b.accountId;
    const chatContactId = (_c = props.channelInfo) === null || _c === void 0 ? void 0 : _c.id;
    const initialChatChannel = chatChannelName && chatAccountId
        ? { name: chatChannelName, id: chatAccountId }
        : null;
    const [currentReply, setCurrentReply] = useState(null);
    const [status, setStatus] = useState('list');
    const [isLoading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [chatSelectedChannel, setChatSelectedChannel] = useState(initialChatChannel);
    const [department, setDepartment] = useState(props.showChannelFilter
        ? null
        : localStorage.getItem(LOCAL_STORAGE_KEYS.FILTERS.QUICK_REPLIES_MANAGE_DEPARTMENTS_FILTER) || null);
    const store = useStore();
    const { t } = useTranslation();
    const { closeMenu, noQuickReplies, channelInfo, enableChannelTargets, showChannelFilter } = props;
    const { quickReplies, addQuickReply, editQuickReply, deleteQuickReply, selectedChannel, setSelectedChannel, quickRepliesLoading, quickRepliesLoadError, loadQuickReplies, } = store.quickRepliesStore;
    // Chat context starts with the active conversation channel
    const chatChannel = channelInfo ? chatSelectedChannel : null;
    // Sidebar context filters the list and seeds new replies
    const activeChannel = showChannelFilter ? selectedChannel : chatChannel;
    const onChannelChanged = channelInfo ? setChatSelectedChannel : setSelectedChannel;
    useEffect(() => {
        setChatSelectedChannel(chatChannelName && chatAccountId ? { name: chatChannelName, id: chatAccountId } : null);
        setCurrentReply(null);
        setStatus('list');
    }, [chatChannelName, chatAccountId, chatContactId]);
    const onDepartmentChanged = (value) => {
        setDepartment(value);
        // Keep sidebar filters separate from chat filters
        if (!showChannelFilter) {
            updateLocalStorage(LOCAL_STORAGE_KEYS.FILTERS.QUICK_REPLIES_MANAGE_DEPARTMENTS_FILTER, value);
        }
    };
    const matchesChannel = (reply) => !activeChannel ||
        reply.send.target.some((target) => target.name === activeChannel.name && target.accountId === activeChannel.id);
    const filteredReplies = quickReplies.filter((reply) => matchesQuickReplyDepartment(reply, department) &&
        matchesQuickReplySearch(reply, search) &&
        matchesChannel(reply));
    const hasActiveFilters = !!activeChannel || !!search || !!department;
    const emptyStateI18nKey = quickReplies.length > 0 && hasActiveFilters
        ? 'noQuickRepliesMatchFilters'
        : 'noQuickReplies';
    useEffect(() => {
        if (noQuickReplies) {
            setCurrentReply(null);
        }
    }, []);
    const onChange = (reply, isNew) => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b, _c;
        setLoading(true);
        try {
            if (isNew) {
                yield addQuickReply({
                    send: reply.send,
                    display: reply.display,
                });
            }
            else {
                const id = (_a = quickReplies.find((r) => r._id === reply._id)) === null || _a === void 0 ? void 0 : _a._id;
                if (!id) {
                    console.error('Quick reply not found', reply);
                    return;
                }
                yield editQuickReply(id, {
                    send: reply.send,
                    display: reply.display,
                });
            }
            reset();
        }
        catch (error) {
            const serverMessage = (_c = (_b = error === null || error === void 0 ? void 0 : error.response) === null || _b === void 0 ? void 0 : _b.data) === null || _c === void 0 ? void 0 : _c.error;
            antMessage.error(serverMessage || t('failedToSaveQuickReply'));
        }
        finally {
            setLoading(false);
        }
    });
    const deleteReplyFunc = (id) => __awaiter(void 0, void 0, void 0, function* () {
        setLoading(true);
        try {
            yield deleteQuickReply(id);
            reset();
        }
        catch (error) {
            antMessage.error(t('unexpectedErrorTryAgain'));
        }
        finally {
            setLoading(false);
        }
    });
    const openAddNewReply = () => {
        setStatus('add');
    };
    const reset = () => {
        setCurrentReply(null);
        setStatus('list');
    };
    const setReplyEditted = (reply, isEditing) => {
        if (!isEditing) {
            reset();
        }
        else {
            setCurrentReply(reply);
            setStatus('edit');
        }
    };
    const setReplyDeleting = (reply, isDeleting) => {
        if (!isDeleting) {
            reset();
        }
        else {
            setCurrentReply(reply);
            setStatus('delete');
        }
    };
    return (React.createElement("div", { className: "quickRepliesWrapper", dir: getDirection() },
        status === 'list' && (React.createElement(React.Fragment, null,
            React.createElement(Header, { onClose: closeMenu, channelSelect: activeChannel || showChannelFilter ? (React.createElement(ChannelSelect, { accounts: store.channels.accounts, activeChannel: activeChannel, onChannelClick: onChannelChanged, className: "quickRepliesHeaderChannelSelect", showAll: false, visible: true })) : undefined }),
            React.createElement(Filters, { search: search, onSearchChanged: setSearch, department: department, onDepartmentChanged: onDepartmentChanged, loading: isLoading }),
            (isLoading || quickRepliesLoading) && (React.createElement("div", { className: "quick-replies-loader-container" },
                React.createElement(LoadingOutlined, { className: "quick-replies-loader-spinner" }))),
            React.createElement("div", { className: 'existingReplies' }, quickRepliesLoadError ? (React.createElement("div", { className: 'noRepliesContainer' },
                React.createElement("div", { className: 'noRepliesBody' },
                    React.createElement(Text, { strong: true }, t('loadingError')),
                    React.createElement(Button, { onClick: () => loadQuickReplies() }, t('retry'))))) : filteredReplies.length ? (filteredReplies.map((reply) => (React.createElement(QuickRepliesField, { key: reply._id, reply: reply, departmentsList: store.departmentsStore.departments, isEditing: (currentReply === null || currentReply === void 0 ? void 0 : currentReply._id) === reply._id, setReplyEditted: setReplyEditted, setReplyDeleting: setReplyDeleting })))) : (React.createElement("div", { className: 'noRepliesContainer' },
                React.createElement("div", { className: 'noRepliesBody' },
                    React.createElement(Icon, { className: 'noRepliesIcon', component: QuickRepliesFolder }),
                    React.createElement("div", { className: 'noRepliesBodyText' },
                        React.createElement(Text, { strong: true },
                            React.createElement(Trans, { i18nKey: emptyStateI18nKey }))))))),
            React.createElement("div", { className: 'ReplyBottom' },
                React.createElement(Button, { className: "big", onClick: openAddNewReply },
                    React.createElement(Icon, { component: QuickRepliesPlus }),
                    React.createElement(Text, { strong: true },
                        React.createElement(Trans, { i18nKey: "createNewQuickReply" })))))),
        React.createElement("div", { className: 'replyEditBoxWrapper' },
            status === 'delete' && currentReply && (React.createElement(DeleteConfirmation, { reply: currentReply, onCancel: reset, deleteReply: () => currentReply && deleteReplyFunc(currentReply._id), loading: isLoading })),
            (status === 'add' || status === 'edit') && (React.createElement(ConfigProvider, { direction: getDirection() },
                React.createElement(AddOrEditReplyField, { reply: status === 'edit' ? currentReply : null, onChange: onChange, onCancel: reset, isLoading: isLoading, channelInfo: channelInfo, enableChannelTargets: enableChannelTargets || !!channelInfo, defaultTargets: activeChannel ? [{ name: activeChannel.name, accountId: activeChannel.id }] : [] }))))));
};
/**
 * QuickRepliesMenu Component
 *
 * @description Component that renders the QuickReplies list, with add/edit/delete functionality.
 * @param closeMenu The function to close the QuickRepliesMenu.
 * @param noQuickReplies The boolean to check if there are no replies.

 * @returns {JSX.Element} The rendered component.
 */
export default observer(QuickRepliesMenu);
